import React, { Suspense, createContext, lazy, useContext } from "react";

import {
  memberMigrationMode,
  memberMigrationRecoveryEligible,
  memberMigrationSignerMatches,
  type MemberMigrationMode,
  type MemberMigrationState,
} from "@/lib/member-bootstrap";
import { sponsorMemberOperation } from "@/lib/member-wallet-transport";
import {
  recoverPendingProviderNeutralSmartAccountOperation,
  sendProviderNeutralSmartAccountTransaction,
  signProviderNeutralSmartAccountMessage,
  type AccountMessageTypedData,
} from "@/lib/provider-neutral-smart-account";

const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const WATCHER_HQ_AUTH_TOKEN_URL =
  "https://watchersociety.com/api/wallet/auth/session-token";
const SUPPORTED_CHAIN_IDS = new Set([8453]);

export type MemberSignerTransaction = {
  chainId: number;
  to: `0x${string}`;
  data: `0x${string}`;
  value: bigint;
  /** Opaque server-minted capability binding this exact transfer to its product intent. */
  intentBindingToken?: string;
};

export type MemberSigner = {
  source: "privy" | "migration";
  status: "ready" | "fallback";
  /** Existing canonical Watcher smart-account address. */
  address: string;
  /** Key-bearing address. A Privy EOA must never replace address. */
  signerAddress: string;
  canSendTransaction: boolean;
  signMessage: (request: { message: string }) => Promise<string>;
  signCandidateMessage: (request: {
    message: string;
    purpose: "existing-account-admin-migration";
  }) => Promise<{
    signature: string;
    canonicalAccountAddress: string;
    signerAddress: string;
    semantics: "eoa";
  }>;
  sendTransaction: (
    transaction: MemberSignerTransaction,
  ) => Promise<{ transactionHash: string; userOperationHash?: string }>;
  recoverPendingTransaction: () => Promise<{
    transactionHash: string;
    userOperationHash: string;
  }>;
  sponsorUserOperation: (
    chainId: number,
    userOperation: object,
  ) => ReturnType<typeof sponsorMemberOperation>;
  recoverSession: () => Promise<void>;
  logout: () => Promise<void>;
};

type CanonicalMemberSession = {
  canonicalAccountAddress: string | null;
  authenticated: boolean;
  isLoading: boolean;
  getAuthToken: (() => Promise<string>) | null;
};

export type PrivyRuntime = {
  sdkEnabled: boolean;
  canaryEligible: boolean;
  status: "disabled" | "restoring" | "ready" | "unavailable";
  canonicalAccountAddress: string | null;
  candidateAddress: string | null;
  originalAdminAddress: string | null;
  setCanonicalMemberSession: (session: CanonicalMemberSession) => void;
  signCandidateMessage: (message: string) => Promise<string>;
  signCandidateRawHash: (hash: `0x${string}`) => Promise<string>;
  signCandidateAccountMessage: (
    data: AccountMessageTypedData,
  ) => Promise<string>;
  recoverSession: () => Promise<void>;
  logout: () => Promise<void>;
};

export type PrivyMemberSignerConfig = {
  sdkEnabled: boolean;
  appId: string;
  bindings: ReadonlyMap<string, string>;
  reason:
    | "ready"
    | "disabled"
    | "missing-app-id"
    | "missing-bindings"
    | "invalid-bindings";
};

function unavailable(): Promise<never> {
  return Promise.reject(new Error("MEMBER_SIGNER_UNAVAILABLE"));
}

const DISABLED_RUNTIME: PrivyRuntime = {
  sdkEnabled: false,
  canaryEligible: false,
  status: "disabled",
  canonicalAccountAddress: null,
  candidateAddress: null,
  originalAdminAddress: null,
  setCanonicalMemberSession: () => undefined,
  signCandidateMessage: unavailable,
  signCandidateRawHash: unavailable,
  signCandidateAccountMessage: unavailable,
  recoverSession: async () => undefined,
  logout: async () => undefined,
};

export const PrivyRuntimeContext =
  createContext<PrivyRuntime>(DISABLED_RUNTIME);

const PrivySdkRuntime =
  import.meta.env.NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_ENABLED === "true"
    ? lazy(() =>
        import("./PrivyMemberSignerRuntime").then((module) => ({
          default: module.PrivyMemberSignerRuntime,
        })),
      )
    : null;

export function parsePrivyCanaryBindings(value: string | undefined): {
  valid: boolean;
  bindings: ReadonlyMap<string, string>;
} {
  const bindings = new Map<string, string>();
  const candidates = new Set<string>();
  if (!value?.trim()) return { valid: true, bindings };

  for (const rawEntry of value.split(",")) {
    const parts = rawEntry.split("=").map((part) => part.trim());
    if (
      parts.length !== 2 ||
      !EVM_ADDRESS.test(parts[0]) ||
      !EVM_ADDRESS.test(parts[1])
    ) {
      return { valid: false, bindings: new Map() };
    }
    const canonicalAddress = parts[0].toLowerCase();
    const candidateAddress = parts[1].toLowerCase();
    if (bindings.has(canonicalAddress) || candidates.has(candidateAddress)) {
      return { valid: false, bindings: new Map() };
    }
    bindings.set(canonicalAddress, candidateAddress);
    candidates.add(candidateAddress);
  }

  return { valid: true, bindings };
}

export function resolvePrivyMemberSignerConfig(env: {
  NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_ENABLED?: string;
  NEXT_PUBLIC_PRIVY_APP_ID?: string;
  NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_CANARY_BINDINGS?: string;
}): PrivyMemberSignerConfig {
  const appId = env.NEXT_PUBLIC_PRIVY_APP_ID?.trim() ?? "";
  const parsed = parsePrivyCanaryBindings(
    env.NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_CANARY_BINDINGS,
  );
  if (env.NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_ENABLED !== "true") {
    return {
      sdkEnabled: false,
      appId,
      bindings: parsed.bindings,
      reason: "disabled",
    };
  }
  if (!appId) {
    return {
      sdkEnabled: false,
      appId,
      bindings: parsed.bindings,
      reason: "missing-app-id",
    };
  }
  if (!parsed.valid) {
    return {
      sdkEnabled: false,
      appId,
      bindings: parsed.bindings,
      reason: "invalid-bindings",
    };
  }
  if (parsed.bindings.size === 0) {
    return {
      sdkEnabled: false,
      appId,
      bindings: parsed.bindings,
      reason: "missing-bindings",
    };
  }
  return {
    sdkEnabled: true,
    appId,
    bindings: parsed.bindings,
    reason: "ready",
  };
}

export async function fetchPrivyMemberAuthToken(
  memberAuthToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string | undefined> {
  if (memberAuthToken.length > 8192 || !JWT_SHAPE.test(memberAuthToken)) {
    return undefined;
  }
  try {
    const response = await fetchImpl(WATCHER_HQ_AUTH_TOKEN_URL, {
      method: "POST",
      credentials: "omit",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${memberAuthToken}`,
        "Content-Type": "application/json",
      },
      body: "{}",
    });
    if (!response.ok) return undefined;
    const body = (await response.json()) as {
      token?: unknown;
      expiresIn?: unknown;
    };
    if (
      typeof body.token !== "string" ||
      body.token.length > 8192 ||
      !JWT_SHAPE.test(body.token) ||
      body.expiresIn !== 300
    ) {
      return undefined;
    }
    return body.token;
  } catch {
    return undefined;
  }
}

export function selectPrivyCandidateAddress(
  wallets: ReadonlyArray<{ address: string; walletClientType?: string }>,
  expectedAddress: string | null,
): string | null {
  if (!expectedAddress) return null;
  const expected = expectedAddress.toLowerCase();
  const matches = wallets.filter(
    (wallet) =>
      wallet.walletClientType === "privy" &&
      EVM_ADDRESS.test(wallet.address) &&
      wallet.address.toLowerCase() === expected,
  );
  return matches.length === 1 ? matches[0].address : null;
}

export function resolveCanonicalMemberWallet(
  storedWalletAddress: string | null | undefined,
  connectedWalletAddress: string,
): string {
  return storedWalletAddress && EVM_ADDRESS.test(storedWalletAddress)
    ? storedWalletAddress
    : connectedWalletAddress;
}

export function MemberSignerProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const config = resolvePrivyMemberSignerConfig({
    NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_ENABLED: import.meta.env
      .NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_ENABLED,
    NEXT_PUBLIC_PRIVY_APP_ID: import.meta.env.NEXT_PUBLIC_PRIVY_APP_ID,
    NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_CANARY_BINDINGS: import.meta.env
      .NEXT_PUBLIC_PRIVY_MEMBER_SIGNER_CANARY_BINDINGS,
  });
  if (!config.sdkEnabled || !PrivySdkRuntime) {
    return (
      <PrivyRuntimeContext.Provider value={DISABLED_RUNTIME}>
        {children}
      </PrivyRuntimeContext.Provider>
    );
  }

  return (
    <Suspense fallback={null}>
      <PrivySdkRuntime appId={config.appId} bindings={config.bindings}>
        {children}
      </PrivySdkRuntime>
    </Suspense>
  );
}

export function usePrivyMemberSignerRuntime() {
  return useContext(PrivyRuntimeContext);
}

export function createMemberSigner(options: {
  canonicalAccountAddress: string | null;
  privy: PrivyRuntime;
  getMemberAuthToken?: (() => Promise<string>) | null;
  enforceMigrationState?: boolean;
  migrationState?: MemberMigrationState | null;
  getCurrentMigrationState?: (() => Promise<MemberMigrationState>) | null;
}): MemberSigner | null {
  const canonicalAddress = EVM_ADDRESS.test(
    options.canonicalAccountAddress ?? "",
  )
    ? options.canonicalAccountAddress!
    : null;
  if (!canonicalAddress) return null;

  const initialMode = options.enforceMigrationState
    ? memberMigrationMode(options.migrationState ?? null)
    : null;
  const initialRecoveryEligible = Boolean(
    options.enforceMigrationState &&
      memberMigrationRecoveryEligible(options.migrationState ?? null),
  );
  if (
    options.enforceMigrationState &&
    initialMode === "blocked" &&
    !initialRecoveryEligible
  ) {
    return null;
  }

  const privySessionMatches = Boolean(
    options.privy.canonicalAccountAddress &&
      options.privy.canonicalAccountAddress.toLowerCase() ===
        canonicalAddress.toLowerCase(),
  );
  const privyReady = Boolean(
    privySessionMatches &&
      options.privy.status === "ready" &&
      options.privy.candidateAddress,
  );
  if (!privyReady) return null;
  if (
    options.enforceMigrationState &&
    (initialMode === "provider-neutral" || initialRecoveryEligible) &&
    !memberMigrationSignerMatches(
      options.migrationState ?? null,
      options.privy.candidateAddress,
    )
  ) {
    return null;
  }

  const signerAddress = options.privy.candidateAddress!;
  const providerNeutralIdentityReady = Boolean(
    privyReady &&
      options.getMemberAuthToken &&
      options.privy.originalAdminAddress &&
      options.privy.candidateAddress?.toLowerCase() ===
        options.privy.originalAdminAddress.toLowerCase(),
  );
  const providerNeutralReady = Boolean(
    providerNeutralIdentityReady &&
      (!options.enforceMigrationState || initialMode === "provider-neutral"),
  );
  const assertCurrentMode = async (expected: MemberMigrationMode) => {
    if (!options.enforceMigrationState) return;
    const current = options.getCurrentMigrationState
      ? await options.getCurrentMigrationState()
      : options.migrationState;
    if (
      memberMigrationMode(current ?? null) !== expected ||
      (expected === "provider-neutral" &&
        !memberMigrationSignerMatches(
          current ?? null,
          options.privy.candidateAddress,
        ))
    ) {
      throw new Error("MEMBER_MIGRATION_PROVIDER_UNAVAILABLE");
    }
  };
  const assertCurrentRecovery = async () => {
    if (!options.enforceMigrationState) {
      throw new Error("MEMBER_MIGRATION_PROVIDER_UNAVAILABLE");
    }
    const current = options.getCurrentMigrationState
      ? await options.getCurrentMigrationState()
      : options.migrationState;
    if (
      !memberMigrationRecoveryEligible(current ?? null) ||
      !memberMigrationSignerMatches(
        current ?? null,
        options.privy.candidateAddress,
      )
    ) {
      throw new Error("MEMBER_MIGRATION_PROVIDER_UNAVAILABLE");
    }
  };
  const providerNeutralIdentityOptions = providerNeutralIdentityReady
    ? {
        canonicalAccountAddress: canonicalAddress,
        originalAdminAddress: options.privy.originalAdminAddress!,
        candidateSignerAddress: options.privy.candidateAddress!,
        signRawHash: options.privy.signCandidateRawHash,
        signAccountMessage: options.privy.signCandidateAccountMessage,
      }
    : null;
  const providerNeutralOptions =
    providerNeutralReady && providerNeutralIdentityOptions
      ? {
          ...providerNeutralIdentityOptions,
          getMemberAuthToken: async () => {
            await assertCurrentMode("provider-neutral");
            return options.getMemberAuthToken!();
          },
        }
      : null;
  const recoveryOptions =
    providerNeutralIdentityOptions &&
    (providerNeutralReady || initialRecoveryEligible)
      ? {
          ...providerNeutralIdentityOptions,
          getMemberAuthToken: async () => {
            await assertCurrentRecovery();
            return options.getMemberAuthToken!();
          },
        }
      : null;
  return {
    source: providerNeutralReady ? "privy" : "migration",
    status:
      providerNeutralReady ||
      (!options.enforceMigrationState && privyReady) ||
      !options.privy.canaryEligible
        ? "ready"
        : "fallback",
    address: canonicalAddress,
    signerAddress,
    canSendTransaction: Boolean(providerNeutralOptions),
    signMessage: providerNeutralOptions
      ? async (request) => {
          await assertCurrentMode("provider-neutral");
          return await signProviderNeutralSmartAccountMessage(
            providerNeutralOptions,
            request.message,
          );
        }
      : unavailable,
    signCandidateMessage: async ({ message, purpose }) => {
      if (!privyReady || purpose !== "existing-account-admin-migration") {
        throw new Error("PRIVY_MEMBER_SIGNER_PURPOSE_REJECTED");
      }
      if (options.enforceMigrationState) {
        const current = options.getCurrentMigrationState
          ? await options.getCurrentMigrationState()
          : options.migrationState;
        if (memberMigrationMode(current ?? null) !== "legacy-recovery") {
          throw new Error("MEMBER_MIGRATION_PROVIDER_UNAVAILABLE");
        }
      }
      return {
        signature: await options.privy.signCandidateMessage(message),
        canonicalAccountAddress: canonicalAddress,
        signerAddress,
        semantics: "eoa",
      };
    },
    sendTransaction: providerNeutralOptions
      ? async (transaction) => {
          await assertCurrentMode("provider-neutral");
          return await sendProviderNeutralSmartAccountTransaction(
            providerNeutralOptions,
            transaction,
          );
        }
      : async () => {
          throw new Error("PRIVY_EXISTING_ACCOUNT_ADAPTER_NOT_READY");
        },
    recoverPendingTransaction: recoveryOptions
      ? async () => {
          await assertCurrentRecovery();
          return recoverPendingProviderNeutralSmartAccountOperation(
            recoveryOptions,
          );
        }
      : unavailable,
    sponsorUserOperation: async (chainId, userOperation) => {
      if (!privyReady) {
        throw new Error("PRIVY_EXISTING_ACCOUNT_ADAPTER_NOT_READY");
      }
      if (!SUPPORTED_CHAIN_IDS.has(chainId)) {
        throw new Error("MEMBER_SIGNER_CHAIN_UNSUPPORTED");
      }
      await assertCurrentMode("provider-neutral");
      const sender = (userOperation as { sender?: unknown }).sender;
      if (
        typeof sender !== "string" ||
        !EVM_ADDRESS.test(sender) ||
        sender.toLowerCase() !== canonicalAddress.toLowerCase()
      ) {
        throw new Error("WALLET_CONTINUITY_MISMATCH");
      }
      if (!options.getMemberAuthToken) {
        throw new Error("MEMBER_SESSION_UNAVAILABLE");
      }
      const memberAuthToken = await options.getMemberAuthToken();
      return sponsorMemberOperation(chainId, userOperation, memberAuthToken);
    },
    recoverSession: options.privy.recoverSession,
    logout: options.privy.logout,
  };
}
